/**
 * Dev-only preview для edit-primitives. Mount via `?primitive-preview=stepper`
 * query-param (см. App.tsx short-circuit). NOT для production.
 *
 * Каждый primitive получает свой preview block с live local state.
 */
import { useState } from 'react';
import { Stepper } from './Stepper';
import { Toggle } from './Toggle';
import { Cycle } from './Cycle';
import { TapEditText } from './TapEditText';
import { TapEditBlock } from './TapEditBlock';
import { SegmentedPips } from './SegmentedPips';
import { PopoverSelect } from './PopoverSelect';
import { ChipMultiSelect } from './ChipMultiSelect';
import { ListEditor, type ListEditorItem } from './ListEditor';
import { ImageSlot } from './ImageSlot';
import { DerivedDisplay } from './DerivedDisplay';

const containerStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: '#0a0a0a',
  color: '#e5e7eb',
  overflowY: 'auto',
  padding: '32px',
  fontFamily: 'system-ui, sans-serif',
  zIndex: 9999,
};

const sectionStyle: React.CSSProperties = {
  marginBottom: 40,
  padding: '20px',
  background: '#1a1a1a',
  border: '1px solid #2a2a2a',
  borderRadius: 8,
};

const titleStyle: React.CSSProperties = {
  fontSize: 14,
  textTransform: 'uppercase',
  letterSpacing: 1.5,
  color: '#9ca3af',
  marginBottom: 16,
  fontWeight: 600,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 24,
  alignItems: 'flex-end',
  flexWrap: 'wrap',
};

const hintStyle: React.CSSProperties = {
  marginTop: 16,
  fontSize: 12,
  color: '#6b7280',
};

function StepperPreview() {
  const [hp, setHp] = useState(28);
  const [ac, setAc] = useState(15);
  const [level, setLevel] = useState(5);
  const [str, setStr] = useState(13);

  return (
    <section style={sectionStyle}>
      <div style={titleStyle}>Stepper (Pattern 03) — number editing</div>
      <div style={rowStyle}>
        <Stepper value={hp} onChange={setHp} min={0} max={40} label="HP" suffix=" / 40" ariaLabel="hit points" />
        <Stepper value={ac} onChange={setAc} min={0} max={30} label="AC" />
        <Stepper value={level} onChange={setLevel} min={1} max={20} label="Level" />
        <Stepper value={str} onChange={setStr} min={1} max={30} label="STR" />
        <Stepper value={ac} onChange={setAc} label="(disabled)" disabled />
      </div>
      <div style={hintStyle}>
        Try: click number к edit, ↑/↓ arrows = ±1, Shift+↑/↓ = ±10, Enter/blur = commit, Esc = cancel
      </div>
    </section>
  );
}

function TogglePreview() {
  const [inspiration, setInspiration] = useState(false);
  const [advantage, setAdvantage] = useState(true);

  return (
    <section style={sectionStyle}>
      <div style={titleStyle}>Toggle (Pattern 05) — boolean dot toggle</div>
      <div style={rowStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Toggle value={inspiration} onChange={setInspiration} label="Inspiration" />
          <span style={{ fontSize: 12, color: '#9ca3af' }}>Inspiration: {String(inspiration)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Toggle value={advantage} onChange={setAdvantage} label="Advantage" size={16} />
          <span style={{ fontSize: 12, color: '#9ca3af' }}>Advantage: {String(advantage)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Toggle value={false} onChange={() => undefined} label="(disabled)" disabled />
          <span style={{ fontSize: 12, color: '#6b7280' }}>(disabled)</span>
        </div>
      </div>
      <div style={hintStyle}>Click dot к flip; filled = true, hollow = false.</div>
    </section>
  );
}

type ProficiencyState = 'none' | 'proficient' | 'expertise';
const PROF_STATES: readonly ProficiencyState[] = ['none', 'proficient', 'expertise'];

function CyclePreview() {
  const [athletics, setAthletics] = useState<ProficiencyState>('none');
  const [stealth, setStealth] = useState<ProficiencyState>('proficient');
  const [arcana, setArcana] = useState<ProficiencyState>('expertise');

  return (
    <section style={sectionStyle}>
      <div style={titleStyle}>Cycle (Pattern 06) — n-state cycle</div>
      <div style={rowStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Cycle value={athletics} states={PROF_STATES} onChange={setAthletics} label="Athletics" size={16} />
          <span style={{ fontSize: 12, color: '#9ca3af' }}>Athletics: {athletics}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Cycle value={stealth} states={PROF_STATES} onChange={setStealth} label="Stealth" size={16} />
          <span style={{ fontSize: 12, color: '#9ca3af' }}>Stealth: {stealth}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Cycle value={arcana} states={PROF_STATES} onChange={setArcana} label="Arcana" size={16} />
          <span style={{ fontSize: 12, color: '#9ca3af' }}>Arcana: {arcana}</span>
        </div>
      </div>
      <div style={hintStyle}>
        Click к cycle: hollow circle (none) → filled circle (proficient) → diamond (expertise) → wrap.
      </div>
    </section>
  );
}

function TapEditTextPreview() {
  const [name, setName] = useState('Thorin Oakenshield');
  const [race, setRace] = useState('');

  return (
    <section style={sectionStyle}>
      <div style={titleStyle}>TapEditText (Pattern 01) — inline single-line text</div>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ width: 240 }}>
          <TapEditText value={name} onChange={setName} label="Character Name" maxLength={50} />
        </div>
        <div style={{ width: 240 }}>
          <TapEditText value={race} onChange={setRace} label="Race" placeholder="Click to set race…" />
        </div>
      </div>
      <div style={hintStyle}>
        Click text к enter edit mode. Enter/blur = commit, Esc = cancel. Empty string allowed if you clear it.
      </div>
    </section>
  );
}

function TapEditBlockPreview() {
  const [backstory, setBackstory] = useState(
    'Born in the icy peaks of the Lonely Mountain, raised among dragons…',
  );
  const [notes, setNotes] = useState('');

  return (
    <section style={sectionStyle}>
      <div style={titleStyle}>TapEditBlock (Pattern 02) — inline multi-line text</div>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ width: 320 }}>
          <TapEditBlock value={backstory} onChange={setBackstory} label="Backstory" rows={4} />
        </div>
        <div style={{ width: 320 }}>
          <TapEditBlock
            value={notes}
            onChange={setNotes}
            label="Combat Notes"
            placeholder="Click к add notes…"
            rows={3}
          />
        </div>
      </div>
      <div style={hintStyle}>
        Click block к edit. Ctrl/Cmd+Enter = commit, Esc = cancel. Multi-line preserved via white-space: pre-wrap.
      </div>
    </section>
  );
}

function SegmentedPipsPreview() {
  const [insight, setInsight] = useState(3);
  const [stress, setStress] = useState(1);

  return (
    <section style={sectionStyle}>
      <div style={titleStyle}>SegmentedPips (Pattern 04) — pip-row editing</div>
      <div style={rowStyle}>
        <SegmentedPips value={insight} max={5} onChange={setInsight} label="Insight" ariaLabel="insight pips" />
        <SegmentedPips value={stress} max={5} onChange={setStress} label="Stress" ariaLabel="stress pips" />
        <SegmentedPips value={3} max={5} onChange={() => undefined} label="(disabled)" disabled />
      </div>
      <div style={hintStyle}>Click pip к set value; click already-filled last pip к decrement.</div>
    </section>
  );
}

const SHORT_OPTIONS = [
  { value: 'fighter', label: 'Fighter' },
  { value: 'rogue', label: 'Rogue' },
  { value: 'wizard', label: 'Wizard' },
  { value: 'cleric', label: 'Cleric' },
  { value: 'ranger', label: 'Ranger' },
] as const;

const LONG_OPTIONS = [
  { value: 'abjuration', label: 'Abjuration' },
  { value: 'conjuration', label: 'Conjuration' },
  { value: 'divination', label: 'Divination' },
  { value: 'enchantment', label: 'Enchantment' },
  { value: 'evocation', label: 'Evocation' },
  { value: 'illusion', label: 'Illusion' },
  { value: 'necromancy', label: 'Necromancy' },
  { value: 'transmutation', label: 'Transmutation' },
  { value: 'universal', label: 'Universal' },
] as const;

function PopoverSelectPreview() {
  const [classVal, setClassVal] = useState<string | null>(null);
  const [school, setSchool] = useState<string | null>('evocation');

  return (
    <section style={sectionStyle}>
      <div style={titleStyle}>PopoverSelect (Pattern 07) — dropdown с optional search</div>
      <div style={rowStyle}>
        <PopoverSelect
          value={classVal}
          onChange={setClassVal}
          options={SHORT_OPTIONS}
          label="Class (≤7, no search)"
          placeholder="Select class…"
          triggerWidth={180}
        />
        <PopoverSelect
          value={school}
          onChange={setSchool}
          options={LONG_OPTIONS}
          label="School (>7, searchable)"
          placeholder="Select school…"
          searchable
          triggerWidth={200}
        />
      </div>
      <div style={hintStyle}>
        Short list: no search bar. Long list ({'>'}7): search input auto-shown. Arrow keys + Enter navigate.
      </div>
    </section>
  );
}

const TRAIT_OPTIONS = [
  { value: 'brave', label: 'Brave' },
  { value: 'cunning', label: 'Cunning' },
  { value: 'loyal', label: 'Loyal' },
  { value: 'reckless', label: 'Reckless' },
  { value: 'stoic', label: 'Stoic' },
  { value: 'witty', label: 'Witty' },
] as const;

function ChipMultiSelectPreview() {
  const [traits, setTraits] = useState<readonly string[]>([]);
  const [capped, setCapped] = useState<readonly string[]>(['brave', 'stoic']);

  return (
    <section style={sectionStyle}>
      <div style={titleStyle}>ChipMultiSelect (Pattern 08) — multi-value chip picker</div>
      <div style={rowStyle}>
        <ChipMultiSelect
          values={traits}
          onChange={setTraits}
          options={TRAIT_OPTIONS}
          label="Traits (unlimited)"
          placeholder="No traits selected"
        />
        <ChipMultiSelect
          values={capped}
          onChange={setCapped}
          options={TRAIT_OPTIONS}
          label="Traits (max 2)"
          maxValues={2}
          placeholder="No traits selected"
        />
      </div>
      <div style={hintStyle}>
        Click + к open dropdown; click chip × к remove. maxValues disables + button when reached.
      </div>
    </section>
  );
}

interface InventoryItem extends ListEditorItem {
  name: string;
  qty: number;
}

let invIdCounter = 100;
const makeInventoryItem = (): InventoryItem => ({
  id: `inv-${++invIdCounter}`,
  name: '',
  qty: 1,
});

function ListEditorPreview() {
  const [inventory, setInventory] = useState<readonly InventoryItem[]>([
    { id: 'inv-1', name: 'Longsword', qty: 1 },
    { id: 'inv-2', name: 'Healing Potion', qty: 3 },
    { id: 'inv-3', name: 'Torch', qty: 5 },
  ]);

  return (
    <section style={sectionStyle}>
      <div style={titleStyle}>ListEditor (Pattern 09) — list of objects</div>
      <div style={{ maxWidth: 420 }}>
        <ListEditor<InventoryItem>
          items={inventory}
          onChange={setInventory}
          createItem={makeInventoryItem}
          addLabel="+ Add item"
          maxItems={10}
          label="Inventory"
          renderItem={(item, isEditing, onItemChange) =>
            isEditing ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={item.name}
                  placeholder="Item name…"
                  onChange={(e) => onItemChange({ name: e.target.value })}
                  style={{
                    flex: 1,
                    padding: '4px 8px',
                    font: "400 14px 'Fraunces', serif",
                    border: '1px solid var(--paper-rule)',
                    background: '#fff',
                    color: 'var(--paper-ink)',
                  }}
                />
                <input
                  type="number"
                  value={item.qty}
                  min={1}
                  onChange={(e) => onItemChange({ qty: Number(e.target.value) || 1 })}
                  style={{
                    width: 60,
                    padding: '4px 8px',
                    font: "400 14px 'Fraunces', serif",
                    border: '1px solid var(--paper-rule)',
                    background: '#fff',
                    color: 'var(--paper-ink)',
                  }}
                />
              </div>
            ) : (
              <div
                style={{
                  font: "400 14px 'Fraunces', serif",
                  color: 'var(--paper-ink)',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <span>{item.name || <em style={{ opacity: 0.5 }}>(unnamed)</em>}</span>
                <span style={{ color: 'var(--paper-ink-2)' }}>×{item.qty}</span>
              </div>
            )
          }
        />
      </div>
      <div style={hintStyle}>
        Hover row → pencil icon. Click pencil = edit. Click + к add new (auto-enters edit). × = delete. maxItems=10.
      </div>
    </section>
  );
}

function ImageSlotPreview() {
  const [portrait, setPortrait] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  return (
    <section style={sectionStyle}>
      <div style={titleStyle}>ImageSlot (Pattern 10) — image upload via click/drag</div>
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <ImageSlot
          value={portrait}
          onChange={setPortrait}
          label="Portrait"
          size={{ width: 140, height: 180 }}
          placeholder="?"
        />
        <ImageSlot
          value={token}
          onChange={setToken}
          label="Token (square)"
          size={{ width: 100, height: 100 }}
          placeholder="T"
        />
        <ImageSlot
          value={null}
          onChange={() => undefined}
          label="(disabled)"
          size={{ width: 100, height: 100 }}
          disabled
        />
      </div>
      <div style={hintStyle}>
        Click slot к open file picker, или drag-drop image. Right-click or × button к clear. Max 2 МБ.
      </div>
    </section>
  );
}

function DerivedDisplayPreview() {
  const [ac] = useState(17);
  const [spellSave] = useState(15);

  const acParts = [
    { label: 'Base', value: 10, source: 'unarmored' },
    { label: 'Armor', value: 5, source: 'chain mail' },
    { label: 'DEX', value: 0, source: 'dex mod (capped)' },
    { label: 'Shield', value: 2, source: 'equipped' },
  ];

  const spellSaveParts = [
    { label: 'Base', value: 8 },
    { label: 'Prof', value: 3, source: 'level 5' },
    { label: 'INT', value: 4, source: 'INT 18' },
  ];

  return (
    <section style={sectionStyle}>
      <div style={titleStyle}>DerivedDisplay (Pattern 11) — derived value + breakdown popover</div>
      <div style={rowStyle}>
        <DerivedDisplay value={ac} parts={acParts} label="AC" size="normal" trigger="hover" />
        <DerivedDisplay
          value={spellSave}
          parts={spellSaveParts}
          label="Spell Save DC"
          size="large"
          trigger="click"
        />
        <DerivedDisplay value={20} parts={[]} label="No breakdown" size="small" />
      </div>
      <div style={hintStyle}>
        Hover (default) or click к see derivation. Empty parts → no popover. trigger=&quot;click&quot; toggles open/closed.
      </div>
    </section>
  );
}

export function EditPrimitivesPreview() {
  return (
    <div style={containerStyle}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
        Edit Primitives — Live Preview
      </h1>
      <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 24 }}>
        Mounted via <code>?primitive-preview=stepper</code> (или just <code>?primitive-preview</code>).
        Dev-only — НЕ в production navigation. 11 primitives (Canvas P12 = RoleplayBoard, отдельно).
      </p>
      <StepperPreview />
      <TogglePreview />
      <CyclePreview />
      <TapEditTextPreview />
      <TapEditBlockPreview />
      <SegmentedPipsPreview />
      <PopoverSelectPreview />
      <ChipMultiSelectPreview />
      <ListEditorPreview />
      <ImageSlotPreview />
      <DerivedDisplayPreview />
      <div style={{ fontSize: 11, color: '#374151', marginTop: 40 }}>
        12-component plan: Stepper ✓ · Toggle ✓ · Cycle ✓ · TapEditText ✓ · TapEditBlock ✓ · SegmentedPips ✓ · PopoverSelect ✓ · ChipMultiSelect ✓ · ListEditor ✓ · ImageSlot ✓ · DerivedDisplay ✓ · Canvas (in RoleplayBoard)
      </div>
    </div>
  );
}

export default EditPrimitivesPreview;
